import { makeStyles, mergeClasses } from '@fluentui/react-components';

import { palette, tokens, useAppButtonStyles } from '../../theme';
import { ModalBackdrop } from '../modal';

export type ConfirmPromptProps = {
    title: string;
    body?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
};

export const ConfirmPrompt: React.FC<ConfirmPromptProps> = ({
    title,
    body,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    onConfirm,
    onCancel,
}) => {
    const classes = useStyles();
    const buttons = useAppButtonStyles();

    return (
        <ModalBackdrop onClose={onCancel} ariaLabel={title} zIndex={1100}>
            <div className={classes.panel}>
                <h2 className={classes.title}>{title}</h2>
                {body && <p className={classes.body}>{body}</p>}
                <div className={classes.actions}>
                    <button type="button" className={buttons.secondary} onClick={onCancel}>
                        {cancelLabel}
                    </button>
                    <button type="button" className={mergeClasses(buttons.primary, destructive && classes.destructive)} onClick={onConfirm}>
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </ModalBackdrop>
    );
};

const useStyles = makeStyles({
    panel: {
        backgroundColor: palette.panelBg,
        borderRadius: tokens.borderRadiusXLarge,
        boxShadow: palette.dialogShadow,
        width: 'min(90vw, 440px)',
        padding: tokens.spacingHorizontalXL,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    title: {
        margin: 0,
        fontSize: '17px',
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground1,
    },
    body: {
        margin: 0,
        fontSize: '13px',
        color: tokens.colorNeutralForeground2,
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
        marginTop: tokens.spacingVerticalM,
    },
    destructive: {
        backgroundColor: palette.overText,
        '&:hover:not(:disabled)': {
            backgroundColor: palette.destructiveAccentHover,
        },
    },
});
